// ─────────────────────────────────────────────────────────────────────────────
//  Chat streaming markdown machinery.
//
//  Lives here (next to `ChatMarkdown.tsx`) because it is used ONLY by chat:
//  `ChatMessage` feeds its raw streaming buffer through `useStreamingMarkdown`
//  to get a paced, markdown-safe `smoothContent`, which it then hands to
//  `ChatMarkdown` for actual rendering.
//
//  Why a separate file instead of inline in `ChatMarkdown`: the autoclose
//  logic (`stabilizeStreamingMarkdown` and its helpers) is genuinely
//  complicated — closes open `**`/`*`/`` ` ``/```` ``` ````/`~~`, holds back
//  partial link/image syntax, holds back tables without a separator row yet,
//  completes partial rows, holds back trailing marker-only bullet lines. It
//  is isolated here so it can be reasoned about, tested, and changed without
//  touching the renderer component.
//
//  Doc markdown deliberately does NOT use any of this — docs are written
//  atomically (not streamed token-by-token), so there is no half-formed-
//  syntax problem to solve there.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react'

/*
 * STREAMING MARKDOWN PIPELINE
 *
 * Keep the buffer and stabilizer relationship explicit:
 *
 *   raw accumulated message
 *     -> buffer chooses a raw prefix cursor
 *     -> stabilizer makes that raw prefix markdown-safe
 *     -> react-markdown renders it
 *
 * The stabilizer must run AFTER the buffer chooses a raw prefix. Running it
 * before buffering would let synthetic closers and held-back table/link text
 * become part of the cursor math.
 *
 * `useStreamingMarkdown` owns timing/cursor pacing. It drains the raw buffer
 * at a steady client-side cadence, independent of uneven backend deltas.
 * `stabilizeStreamingMarkdown` owns markdown syntax safety for that prefix.
 * They solve different problems and should be changed together when
 * streaming chat UX changes.
 */

interface Marker {
  open: string
  close: string
  /** Inside this marker, suppress nested marker recognition (e.g. inline
   *  code, code fence). The stream literally appends characters until the
   *  matching closer arrives. */
  literal: boolean
}

/** Marker registry, scanned in this order. Longer openers MUST come before
 *  shorter ones that are prefixes (e.g. ``` before `, ** before *) so we
 *  greedily match the longest opener at the cursor. */
const MARKERS: readonly Marker[] = [
  { open: '```', close: '\n```', literal: true },
  { open: '~~~', close: '\n~~~', literal: true },
  { open: '**', close: '**', literal: false },
  { open: '~~', close: '~~', literal: false },
  { open: '`', close: '`', literal: true },
  { open: '*', close: '*', literal: false }
]

interface StackEntry {
  marker: Marker
  /** Index in `output` where the synthetic close starts. We bump this
   *  whenever new characters are inserted at or before its position so the
   *  pop step can step over it precisely. */
  closeStart: number
}

/** Pure stabilizer: replays the entire input from scratch. O(N) per call.
 *  Streaming callers pass a strictly-growing `input` each frame; the
 *  output for a given input is deterministic, so per-frame replay is
 *  idempotent and safe (no cross-frame state leaks). */
export function stabilizeStreamingMarkdown(input: string): string {
  if (!input) return ''
  return stabilizeBulletLists(stabilizeTables(stabilizeInline(input)))
}

function stabilizeInline(input: string): string {
  let output = ''
  let insertion = 0
  const stack: StackEntry[] = []

  // Link/image holdback: when we enter a `[` or `![`, we buffer subsequent
  // input WITHOUT emitting until the link is proven complete or proven
  // non-link. `linkStart` is the index in `input` where the bracket lives.
  let linkStart: number | null = null
  type LinkPhase = 'label' | 'after-label' | 'url'
  let linkPhase: LinkPhase = 'label'

  // Pending opener: an opener we've consumed from input but NOT yet emitted
  // to `output`, because we don't yet know if any content will follow.
  //
  // GUARD: this prevents empty-marker flicker. When the lerp reveals just
  // `**`, emitting `**` plus synthetic close `**` produces `****`, which
  // CommonMark can render as literal text in table cells.
  let pendingOpener: Marker | null = null

  let i = 0
  while (i < input.length) {
    // Link / image holdback.
    if (linkStart !== null) {
      const ch = input[i]
      if (linkPhase === 'label') {
        if (ch === ']') {
          linkPhase = 'after-label'
          i += 1
          continue
        }
        if (ch === '\n') {
          ;[output, insertion] = insertText(output, insertion, stack, input.slice(linkStart, i))
          linkStart = null
          linkPhase = 'label'
          continue
        }
        i += 1
        continue
      }
      if (linkPhase === 'after-label') {
        if (ch === '(') {
          linkPhase = 'url'
          i += 1
          continue
        }
        ;[output, insertion] = insertText(output, insertion, stack, input.slice(linkStart, i))
        linkStart = null
        linkPhase = 'label'
        continue
      }
      if (ch === ')') {
        ;[output, insertion] = insertText(output, insertion, stack, input.slice(linkStart, i + 1))
        linkStart = null
        linkPhase = 'label'
        i += 1
        continue
      }
      if (ch === '\n' || ch === ' ' || ch === '\t') {
        ;[output, insertion] = insertText(output, insertion, stack, input.slice(linkStart, i))
        linkStart = null
        linkPhase = 'label'
        continue
      }
      i += 1
      continue
    }

    // Resolve any pending opener before processing this character.
    if (pendingOpener) {
      if (input.startsWith(pendingOpener.close, i)) {
        i += pendingOpener.close.length
        pendingOpener = null
        continue
      }
      const insertChunk = pendingOpener.open + pendingOpener.close
      output = output.slice(0, insertion) + insertChunk + output.slice(insertion)
      for (const s of stack) {
        if (s.closeStart >= insertion) s.closeStart += insertChunk.length
      }
      const closeStart = insertion + pendingOpener.open.length
      stack.push({ marker: pendingOpener, closeStart })
      insertion = closeStart
      pendingOpener = null
    }

    const top = stack[stack.length - 1]
    const insideLiteral = top?.marker.literal ?? false

    if (top) {
      const closeMatchLen = matchCloseAt(input, i, top.marker)
      if (closeMatchLen > 0) {
        insertion = top.closeStart + top.marker.close.length
        i += closeMatchLen
        stack.pop()
        continue
      }
    }

    if (!insideLiteral) {
      // Opener-prefix holdback: if the raw prefix ends halfway through a
      // multi-char opener (`*` of `**`, `~` of `~~`, or partial fence), keep
      // it hidden until the next frame proves whether it is markdown syntax
      // or literal punctuation.
      if (isStrictPrefixOfMultiCharOpenerAt(input, i)) {
        break
      }

      if (input.startsWith('![', i)) {
        linkStart = i
        linkPhase = 'label'
        i += 2
        continue
      }
      if (input[i] === '[') {
        linkStart = i
        linkPhase = 'label'
        i += 1
        continue
      }

      const opener = matchOpenerAt(input, i)
      if (opener) {
        pendingOpener = opener
        i += opener.open.length
        continue
      }
    }

    // Close-prefix holdback: if stack top is bold with close `**` and the
    // only remaining char is `*`, hold it back instead of rendering a stray
    // content asterisk for one frame.
    if (top && top.marker.close.length > 1) {
      const closeMarker = top.marker.close
      const remaining = input.slice(i)
      if (
        remaining.length > 0 &&
        remaining.length < closeMarker.length &&
        closeMarker.startsWith(remaining)
      ) {
        break
      }
    }

    const ch = input[i]
    output = output.slice(0, insertion) + ch + output.slice(insertion)
    for (const s of stack) {
      if (s.closeStart >= insertion) s.closeStart += 1
    }
    insertion += 1
    i += 1
  }

  if (linkStart !== null) {
    const heldLinkText = input.slice(linkStart)
    if (linkPhase === 'url') {
      ;[output, insertion] = insertText(output, insertion, stack, `${heldLinkText})`)
    } else if (linkPhase === 'after-label') {
      ;[output, insertion] = insertText(output, insertion, stack, heldLinkText)
    }
  }

  return moveTrailingWhitespaceOutsideSyntheticClosers(output, stack)
}

function moveTrailingWhitespaceOutsideSyntheticClosers(
  output: string,
  stack: StackEntry[]
): string {
  // CommonMark emphasis cannot close after trailing whitespace. A streaming
  // prefix like `**Anypoint ` would otherwise stabilize to `**Anypoint **`,
  // which renders as literal asterisks. Move trailing whitespace outside the
  // synthetic closer: `**Anypoint** `.
  for (let i = stack.length - 1; i >= 0; i--) {
    const { marker, closeStart } = stack[i]
    if (marker.literal) continue
    if (!output.startsWith(marker.close, closeStart)) continue

    let whitespaceStart = closeStart
    while (whitespaceStart > 0 && /\s/.test(output[whitespaceStart - 1])) {
      whitespaceStart--
    }
    if (whitespaceStart === closeStart) continue

    const whitespace = output.slice(whitespaceStart, closeStart)
    output =
      output.slice(0, whitespaceStart) +
      marker.close +
      whitespace +
      output.slice(closeStart + marker.close.length)
  }

  return output
}

/** Word-boundary aware italic check: `*` only opens emphasis when it's not
 *  surrounded by alphanumerics on both sides (markdown left-flanking rule).
 *  This avoids false positives on `5 * 3` or `a*b*c`. */
function isItalicOpener(input: string, i: number): boolean {
  if (input[i] !== '*' || input[i + 1] === '*') return false
  const prev = i > 0 ? input[i - 1] : ' '
  const next = i + 1 < input.length ? input[i + 1] : ' '
  const prevIsWord = /\w/.test(prev)
  const nextIsWord = /\w/.test(next)
  if (prevIsWord && nextIsWord) return false
  if (next === ' ' || next === '\t' || next === '\n') return false
  return true
}

function matchOpenerAt(input: string, i: number): Marker | null {
  for (const m of MARKERS) {
    if (!input.startsWith(m.open, i)) continue
    if (m.open === '*' && !isItalicOpener(input, i)) continue
    return m
  }
  return null
}

function isStrictPrefixOfMultiCharOpenerAt(input: string, i: number): boolean {
  const remaining = input.slice(i)
  if (!remaining) return false

  return MARKERS.some(
    marker =>
      marker.open.length > 1 &&
      remaining.length < marker.open.length &&
      marker.open.startsWith(remaining)
  )
}

function matchCloseAt(input: string, i: number, marker: Marker): number {
  if (input.startsWith(marker.close, i)) return marker.close.length
  if (marker.literal && marker.close.startsWith('\n')) {
    const trimmed = marker.close.slice(1)
    if (input.startsWith(trimmed, i)) return trimmed.length
  }
  return 0
}

function insertText(
  output: string,
  insertion: number,
  stack: StackEntry[],
  text: string
): [string, number] {
  if (!text) return [output, insertion]
  output = output.slice(0, insertion) + text + output.slice(insertion)
  for (const s of stack) {
    if (s.closeStart >= insertion) s.closeStart += text.length
  }
  return [output, insertion + text.length]
}

/** Cross-line table holdback.
 *
 *  Strategy on the trailing block of `|`-prefixed lines (outside fences):
 *    - No separator row yet: hold the entire block back from output because
 *      react-markdown cannot identify a table yet.
 *    - Separator present but trailing body row incomplete: synthesize the
 *      closing `|` so the row streams smoothly as a table row.
 *    - Cross-row `**` span unresolved: hold rows from the unresolved opener.
 */
function stabilizeTables(input: string): string {
  if (!input.includes('|')) return input

  const lines = input.split('\n')
  const fenceMask = computeFenceMask(lines)

  let blockEnd = lines.length - 1
  while (blockEnd >= 0 && lines[blockEnd] === '') blockEnd--
  if (blockEnd < 0) return input

  if (fenceMask[blockEnd]) return input
  if (!isTableLine(lines[blockEnd])) return input

  let blockStart = blockEnd
  while (blockStart - 1 >= 0 && !fenceMask[blockStart - 1] && isTableLine(lines[blockStart - 1])) {
    blockStart--
  }

  const tableLines = lines.slice(blockStart, blockEnd + 1)
  const headerCellCount = countTableCells(tableLines[0])
  const separatorIdx = tableLines.findIndex(
    line => isTableSeparator(line) && countTableCells(line) === headerCellCount
  )

  if (separatorIdx === -1) {
    return lines.slice(0, blockStart).join('\n')
  }

  const lastRow = tableLines[tableLines.length - 1]
  if (!lastRow.trimEnd().endsWith('|')) {
    const completedRow = completePartialTableRow(lastRow)
    if (completedRow === null) {
      return lines.slice(0, blockEnd).join('\n')
    }

    const completedLines = [...lines]
    completedLines[blockEnd] = completedRow
    return completedLines.join('\n')
  }

  const firstDataRowIdx = separatorIdx + 1
  if (firstDataRowIdx < tableLines.length) {
    const unresolvedStartInDataRows = findUnresolvedDoubleAsteriskSpanStart(
      tableLines.slice(firstDataRowIdx)
    )
    if (unresolvedStartInDataRows !== null) {
      const unresolvedAbsoluteLine = blockStart + firstDataRowIdx + unresolvedStartInDataRows
      return lines.slice(0, unresolvedAbsoluteLine).join('\n')
    }
  }

  return input
}

function completePartialTableRow(row: string): string | null {
  const trimmed = row.trim()
  if (!trimmed.startsWith('|')) return null

  const contentAfterOpeningPipe = trimmed.slice(1).trim()
  if (!contentAfterOpeningPipe) return null

  return `${row.trimEnd()} |`
}

function findUnresolvedDoubleAsteriskSpanStart(rows: string[]): number | null {
  let parity = 0
  let spanStart: number | null = null

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const pairCount = countUnescapedDoubleAsterisks(row)
    if (pairCount % 2 === 0) continue

    if (parity === 0) spanStart = i
    parity = 1 - parity
    if (parity === 0) spanStart = null
  }

  return parity === 1 ? spanStart : null
}

function countUnescapedDoubleAsterisks(line: string): number {
  let count = 0
  for (let i = 0; i < line.length - 1; i++) {
    if (line[i] !== '*' || line[i + 1] !== '*') continue
    if (i > 0 && line[i - 1] === '\\') continue
    count++
    i += 1
  }
  return count
}

function countTableCells(row: string): number {
  const trimmed = row.trim()
  if (!trimmed.startsWith('|')) return 0
  const inner = trimmed.endsWith('|') ? trimmed.slice(1, -1) : trimmed.slice(1)
  return inner.split('|').length
}

function computeFenceMask(lines: string[]): boolean[] {
  const mask = new Array(lines.length).fill(false)
  let inFence = false
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trimStart()
    const isFence = trimmed.startsWith('```') || trimmed.startsWith('~~~')
    if (isFence) {
      mask[i] = true
      inFence = !inFence
      continue
    }
    if (inFence) mask[i] = true
  }
  return mask
}

function isTableLine(line: string): boolean {
  return line.trimStart().startsWith('|')
}

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim()
  if (!trimmed.startsWith('|')) return false
  return /^\|(\s*:?-{3,}:?\s*\|)+\s*$/.test(trimmed)
}

/** Hold back trailing incomplete unordered-list items. */
function stabilizeBulletLists(input: string): string {
  if (!input.includes('-') && !input.includes('*') && !input.includes('+')) return input

  const lines = input.split('\n')
  const fenceMask = computeFenceMask(lines)
  const bulletLinePattern = /^(\s*)[-*+](?:\s(.*))?$/
  const markerOnlyContent = new Set(['', '*', '**', '~~', '`', '```', '~~~'])

  let trailingIdx = lines.length - 1
  while (trailingIdx >= 0 && lines[trailingIdx] === '') trailingIdx--
  if (trailingIdx < 0 || fenceMask[trailingIdx]) return input

  const trailingLine = lines[trailingIdx]
  const match = trailingLine.match(bulletLinePattern)
  if (!match) return input

  const content = (match[2] ?? '').trim()
  if (!markerOnlyContent.has(content)) return input

  return lines.slice(0, trailingIdx).join('\n')
}

const STREAM_FRAME_MS = 16
const STREAM_CHARS_PER_SECOND = 120
const STREAM_MAX_CHARS_PER_FRAME = 8

export interface StreamingMarkdownTextDelta {
  index: number
  delta: string
  deltaLength: number
  accumulatedLength: number
  frameId?: number
  chunkId?: number
  chunkTotal?: number
  receivedAtMs: number
}

export interface StreamingMarkdownResult {
  content: string
  rawPrefixLength: number
  isComplete: boolean
}

interface StreamingMarkdownRevealCacheEntry {
  cursor: number
  displayed: string
}

const streamingMarkdownRevealCache = new Map<string, StreamingMarkdownRevealCacheEntry>()
const STREAMING_MARKDOWN_REVEAL_CACHE_LIMIT = 100

function setStreamingMarkdownRevealCache(
  cacheKey: string | undefined,
  entry: StreamingMarkdownRevealCacheEntry
) {
  if (!cacheKey) return

  if (!streamingMarkdownRevealCache.has(cacheKey)) {
    while (streamingMarkdownRevealCache.size >= STREAMING_MARKDOWN_REVEAL_CACHE_LIMIT) {
      const oldestKey = streamingMarkdownRevealCache.keys().next().value
      if (!oldestKey) break
      streamingMarkdownRevealCache.delete(oldestKey)
    }
  }

  streamingMarkdownRevealCache.set(cacheKey, entry)
}

/**
 * Per-stream monotonic high-water mark for the streamed-and-displayed string.
 *
 * The stabilizer can temporarily SHRINK its output mid-stream — most painfully
 * in `stabilizeTables` when an unresolved `**` mid-cell hides several body
 * rows until the closing `**` arrives, only to re-show them. The rendered DOM
 * loses height then regains it, fighting `useTurnScrollPin`'s bottom-tracking
 * and giving the user a violent up-and-down jerk while they scroll to catch
 * up with a table being streamed. Holding the previous (longer) displayed
 * until the new stabilizer output catches up turns the oscillation into a
 * brief freeze, which the pin tolerates fine.
 *
 * Resolution is by length: in an append-only stream, the stabilized output
 * normally only grows, so length is a faithful proxy for "DOM mass". A
 * shorter candidate is the bug-shape we're absorbing.
 */
export function pickMonotonicDisplayed(prev: string, candidate: string): string {
  return candidate.length < prev.length ? prev : candidate
}

export function useStreamingMarkdown(
  targetText: string,
  isActive: boolean,
  cacheKey?: string
): StreamingMarkdownResult {
  const targetRef = useRef(targetText)
  const isActiveRef = useRef(isActive)
  const shouldSmoothRef = useRef(isActive)
  const cachedState = cacheKey ? streamingMarkdownRevealCache.get(cacheKey) : undefined
  const initialCursor = isActive
    ? Math.min(Math.max(cachedState?.cursor ?? 0, 2), targetText.length)
    : targetText.length
  const cursorRef = useRef(initialCursor)
  const initialDisplayed = isActive
    ? cachedState?.displayed && cachedState.cursor === initialCursor
      ? cachedState.displayed
      : stabilizeStreamingMarkdown(targetText.slice(0, initialCursor))
    : targetText
  const [displayed, setDisplayed] = useState(initialDisplayed)
  // Per-stream monotonic high-water mark for the displayed string. See
  // `pickMonotonicDisplayed` below. Reset on cacheKey change in the
  // `cacheKey`-deps effect.
  const lastDisplayedRef = useRef(initialDisplayed)
  const rafRef = useRef<number | null>(null)
  const lastFrameTimeRef = useRef(0)
  const charCarryRef = useRef(0)

  const stabilizeRawPrefix = useCallback(
    (rawPrefixLength: number) =>
      stabilizeStreamingMarkdown(targetRef.current.slice(0, rawPrefixLength)),
    []
  )

  const startLoop = useCallback(() => {
    if (rafRef.current !== null) return

    const animate = (now: number) => {
      const target = targetRef.current
      const cursor = cursorRef.current

      if (cursor < target.length) {
        const elapsedMs =
          lastFrameTimeRef.current === 0 ? STREAM_FRAME_MS : now - lastFrameTimeRef.current

        if (lastFrameTimeRef.current !== 0 && elapsedMs < STREAM_FRAME_MS) {
          rafRef.current = requestAnimationFrame(animate)
          return
        }

        lastFrameTimeRef.current = now
        const remaining = target.length - cursor
        const exactCharsToAdd = (elapsedMs / 1000) * STREAM_CHARS_PER_SECOND + charCarryRef.current
        const wholeCharsToAdd = Math.floor(exactCharsToAdd)
        if (wholeCharsToAdd < 1) {
          charCarryRef.current = exactCharsToAdd
          rafRef.current = requestAnimationFrame(animate)
          return
        }

        const charsToAdd = Math.min(remaining, wholeCharsToAdd, STREAM_MAX_CHARS_PER_FRAME)
        charCarryRef.current =
          charsToAdd === STREAM_MAX_CHARS_PER_FRAME ? 0 : exactCharsToAdd - charsToAdd
        const newCursor = cursor + charsToAdd
        cursorRef.current = newCursor
        const candidate = stabilizeRawPrefix(newCursor)
        const chosen = pickMonotonicDisplayed(lastDisplayedRef.current, candidate)
        lastDisplayedRef.current = chosen
        setDisplayed(chosen)
        setStreamingMarkdownRevealCache(cacheKey, {
          cursor: newCursor,
          displayed: chosen
        })
        rafRef.current = requestAnimationFrame(animate)
      } else {
        rafRef.current = null
        lastFrameTimeRef.current = 0
        charCarryRef.current = 0
        if (!isActiveRef.current) {
          shouldSmoothRef.current = false
          // Stream has finished — snap to the authoritative final stabilized
          // output (no monotonic guard; this is the true end-state).
          const nextDisplayed = stabilizeStreamingMarkdown(target)
          lastDisplayedRef.current = nextDisplayed
          setDisplayed(nextDisplayed)
          setStreamingMarkdownRevealCache(cacheKey, {
            cursor: target.length,
            displayed: nextDisplayed
          })
        }
      }
    }

    rafRef.current = requestAnimationFrame(animate)
  }, [cacheKey, stabilizeRawPrefix])

  // Sync the latest targetText into the ref synchronously during render so
  // the animation loop (and any in-flight `stabilizeRawPrefix(cursor)`
  // callers) read the up-to-date target. The ref-write is a deliberate
  // render-phase mutation; it is idempotent and only ever advances.
  targetRef.current = targetText

  // Kick the animation loop from an effect, NOT from the render body. An
  // earlier version called `startLoop()` here in render, which produced an
  // orphaned RAF handle: when this component re-rendered before the
  // `isActive` effect below ran (or when that effect re-ran due to a
  // changed dep), `rafRef.current` could be overwritten by a second RAF
  // request whose handle the unmount cleanup never saw, so the first loop
  // continued calling stabilizeStreamingMarkdown (O(N) work) every ~16ms
  // after unmount. Driving this from an effect ensures the RAF handle we
  // schedule is the one stored in `rafRef` and is the one cancelled by the
  // unmount cleanup of the `isActive` effect below. We do NOT install a
  // cleanup on this effect itself: the loop should keep running across
  // `targetText` re-renders, and unmount is already handled by the
  // `isActive` effect's cleanup (both effects share the same `rafRef`).
  useEffect(() => {
    if (!isActive) return
    if (targetText.length <= cursorRef.current) return
    if (rafRef.current !== null) return
    startLoop()
  }, [isActive, startLoop, targetText])

  useEffect(() => {
    isActiveRef.current = isActive

    if (!isActive) {
      if (shouldSmoothRef.current && cursorRef.current < targetRef.current.length) {
        startLoop()
        return () => {
          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
          }
        }
      }

      shouldSmoothRef.current = false
      lastFrameTimeRef.current = 0
      charCarryRef.current = 0
      cursorRef.current = targetRef.current.length
      // Stream is no longer active — snap to the authoritative final output
      // and reset the monotonic high-water mark.
      const nextDisplayed = stabilizeStreamingMarkdown(targetRef.current)
      lastDisplayedRef.current = nextDisplayed
      setDisplayed(nextDisplayed)
      setStreamingMarkdownRevealCache(cacheKey, {
        cursor: targetRef.current.length,
        displayed: nextDisplayed
      })
      return
    }

    lastFrameTimeRef.current = 0
    charCarryRef.current = 0
    shouldSmoothRef.current = true
    cursorRef.current = Math.min(cursorRef.current, targetRef.current.length)
    // Stream just (re)started — could be the very first activation or a new
    // cacheKey arriving (different stream). Reset the high-water mark to the
    // stabilized prefix at the current cursor; this is the new baseline.
    const nextDisplayed = stabilizeRawPrefix(cursorRef.current)
    lastDisplayedRef.current = nextDisplayed
    setDisplayed(nextDisplayed)
    setStreamingMarkdownRevealCache(cacheKey, {
      cursor: cursorRef.current,
      displayed: nextDisplayed
    })
    startLoop()

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [cacheKey, isActive, stabilizeRawPrefix, startLoop])

  return {
    content: shouldSmoothRef.current ? displayed : targetText,
    rawPrefixLength: shouldSmoothRef.current ? cursorRef.current : targetText.length,
    isComplete: cursorRef.current >= targetText.length
  }
}

export function isStreamingMarkdownDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const flag = window.localStorage.getItem('chat_debug_markdown')?.toLowerCase()
  return flag === '1' || flag === 'true' || flag === 'on'
}

export function getOddDoubleAsteriskTableRows(text: string): number[] {
  const lines = text.split('\n')
  const oddRows: number[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line.trimStart().startsWith('|')) continue
    const doubles = line.match(/\*\*/g) ?? []
    if (doubles.length % 2 !== 0) oddRows.push(i + 1)
  }
  return oddRows
}

export function buildStreamingMarkdownDeltaDebug(
  rawContent: string,
  textDeltas?: readonly StreamingMarkdownTextDelta[]
) {
  const deltas = textDeltas ?? []
  const reconstructedContent = deltas.map(delta => delta.delta).join('')

  return {
    contentLength: rawContent.length,
    deltaCount: deltas.length,
    deltaStorage:
      deltas.length > 0
        ? 'StreamingMessage.textDeltas captured from SSE data.delta'
        : 'No text deltas on this message; persisted/refetched messages only keep content',
    reconstructedMatchesContent:
      deltas.length > 0 ? reconstructedContent === rawContent : undefined,
    fullContent: rawContent,
    reconstructedContent: deltas.length > 0 ? reconstructedContent : undefined,
    deltas: deltas.map(delta => ({
      index: delta.index,
      frameId: delta.frameId,
      chunkId: delta.chunkId,
      chunkTotal: delta.chunkTotal,
      length: delta.deltaLength,
      accumulatedLength: delta.accumulatedLength,
      receivedAtMs: delta.receivedAtMs,
      delta: delta.delta
    }))
  }
}

export function getTextDeltaBoundary(
  prefixLength: number,
  textDeltas?: readonly StreamingMarkdownTextDelta[]
) {
  if (!textDeltas?.length) return undefined

  const delta = textDeltas.find(delta => prefixLength <= delta.accumulatedLength)
  if (!delta) {
    return {
      prefixLength,
      status: 'after-last-delta',
      deltaCount: textDeltas.length
    }
  }

  const previousAccumulatedLength = delta.accumulatedLength - delta.deltaLength
  const offsetInDelta = prefixLength - previousAccumulatedLength

  return {
    prefixLength,
    deltaIndex: delta.index,
    offsetInDelta,
    deltaLength: delta.deltaLength,
    atDeltaBoundary: offsetInDelta === delta.deltaLength,
    deltaPreview: delta.delta
  }
}

export function getRawMarkdownMarkerLocations(root: HTMLElement | null) {
  if (!root || typeof document === 'undefined' || typeof window === 'undefined') return []

  const locations: Array<{
    marker: string
    text: string
    insideTable: boolean
    insideCode: boolean
    tagPath: string
  }> = []
  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT)

  while (locations.length < 20) {
    const node = walker.nextNode()
    if (!node) break

    const text = node.textContent ?? ''
    const markerMatch = text.match(/\*\*|~~|`/)
    if (!markerMatch) continue

    const parent = node.parentElement
    const tagPath: string[] = []
    let el: HTMLElement | null = parent
    while (el && el !== root.parentElement && tagPath.length < 8) {
      tagPath.push(el.tagName.toLowerCase())
      el = el.parentElement
    }

    locations.push({
      marker: markerMatch[0],
      text,
      insideTable: !!parent?.closest('table'),
      insideCode: !!parent?.closest('code, pre'),
      tagPath: tagPath.join(' > ')
    })
  }

  return locations
}
